import type { MusicVideoDoc } from 'fym-shared/src/music24/musicVideoDoc';
import type {
  BankFootageItem,
  MusicVideoEditorResponse,
  MusicVideoSongMap,
  SerializedMusicVideoCut,
} from 'fym-shared/src/music24/musicVideoApi';
import { useLeaveGuard } from '../smartLinks/catalog/song/page/useLeaveGuard';
import { useCutActions } from './doc/useCutActions';
import { useCutAutosave } from './doc/useCutAutosave';
import type { MusicVideoDocDispatch } from './doc/useMusicVideoDoc';
import { useServerCutSync } from './doc/useServerCutSync';
import { useFootageCatalog } from './footage/useFootageCatalog';
import type { MusicVideoEditorMode } from './MusicVideoEditorContext';
import { useGetMusicVideoCutQuery } from './musicVideoEditorApi';
import { usePreviewTransport } from './preview/usePreviewTransport';

export interface UseEditorSessionProps {
  mode: MusicVideoEditorMode;
  accountId: number;
  catalogItemUuid: string;
  cut: SerializedMusicVideoCut;
  data: MusicVideoEditorResponse;
  songMap: MusicVideoSongMap;
  doc: MusicVideoDoc;
  /** Goes up with each change of the document (`musicVideoDocReducer`). */
  revision: number;
  dispatch: MusicVideoDocDispatch;
  /** The demo artist or a cut that the director writes now: nothing is saved. */
  readOnly: boolean;
  onOpenCut: (cutUuid: string) => void;
  /** Showcase mode: the public audio link that plays in place of the master stream. */
  audioUrl?: string;
  /** Showcase mode: the bank clips of the cut that the showcase answer holds. */
  seedClips?: readonly BankFootageItem[];
}

/**
 * The links of the open editor to the outside: the autosave, the newer cuts of the server, the leave
 * guard, the cut menu actions, the footage and the play clock.
 *
 * In showcase mode (spec 2026-10-10 Studio showcase §4) the edits stay in memory: no cut read, no
 * autosave, no server cut sync, no leave guard, no own footage and no master stream. The bank clips
 * come from the public showcase routes and the audio from `audioUrl`.
 */
export function useEditorSession(props: UseEditorSessionProps) {
  const { mode, accountId, catalogItemUuid, cut, data, songMap, doc, revision, dispatch, readOnly, onOpenCut } = props;
  const isShowcase = mode === 'showcase';
  const cutQuery = useGetMusicVideoCutQuery({ accountId, catalogItemUuid, cutUuid: cut.uuid }, { skip: isShowcase });
  const autosave = useCutAutosave({
    accountId,
    catalogItemUuid,
    cutUuid: cut.uuid,
    doc,
    revision,
    serverRevision: cut.revision,
    enabled: !readOnly && !isShowcase,
    onStale: () => {
      if (!isShowcase) {
        cutQuery.refetch().catch(console.error);
      }
    },
  });
  const durationSec = data.song.durationSec ?? doc.settings.endSec;
  useServerCutSync({
    cut: cutQuery.data?.cut ?? cut,
    durationSec,
    localRevision: revision,
    dispatch,
    autosave,
    enabled: !isShowcase,
  });
  const guard = useLeaveGuard(!readOnly && !isShowcase && autosave.hasUnsaved);
  const cutActions = useCutActions({ accountId, catalogItemUuid, cutUuid: cut.uuid, flush: autosave.flush, onOpenCut });
  const footage = useFootageCatalog(accountId, catalogItemUuid, doc, { mode, seed: props.seedClips });
  const transport = usePreviewTransport({
    accountId,
    catalogItemUuid,
    audioUrl: isShowcase ? props.audioUrl : undefined,
    canPlay: data.song.masterCanPlay,
    range: { startSec: doc.settings.startSec, endSec: doc.settings.endSec },
    downbeats: songMap.downbeats,
  });
  return { autosave, guard, cutActions, footage, transport };
}

export type EditorSession = ReturnType<typeof useEditorSession>;
